import React, { useState, useEffect } from "react";
import PagesHeader from "@components/PagesHeader";
import SectionHeader from "@components/SectionHeader";
import BrandLogo from "../../components/BrandLogo";
import StickyButton from "@components/StickyButton";
import Button from "@components/Button";
import NiceModal from "@ebay/nice-modal-react";
import UnderlineButton from "@components/UnderlineButton";
import { getCouponCatalogueDescribe } from "../../api/coupon";
import {
  PRIZE_CACHE_TIME,
  PRIZE_STALE_TIME,
  getPrizeList,
} from "../../api/prize";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, useLocation } from "react-router";
import {
  getModuleValue,
  parseCMSBold,
  parseHTMLToText,
  useWindowSize,
  isCouponInStock,
  getCouponAvailability,
  shouldRedirectCouponToActivation,
  getActivationActivityPath,
  getCouponRaffleOutroType,
  getCouponRaffleOutroContent,
  getCouponRaffleOutroSectionTitle,
  isCouponPlaceholder,
  findGameRewardPrizeInList,
  getGameRewardPrizeRedeemPath,
} from "../../Helpers";
import "./style.scss";
import {
  DETAILS_CACHE_TIME,
  DETAILS_REFETCH_INVERVAL,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "../../api/consumer";
import {
  COUPON_CACHE_TIME,
  COUPON_STALE_TIME,
  getCouponCatalogueStandardList,
  getCouponList,
} from "../../api/coupon";
import { fetchData } from "../../Helpers";
import DOMPurify from "dompurify";
import WarningIcon from "@assets/warning.svg?react";

export default function RedeemAwardPage() {
  const { width } = useWindowSize();
  const { ident } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  function formatDate(timestamp) {
    const date = new Date(timestamp);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  const { data, refetch: refetchData } = useQuery({
    queryKey: ["coupon-catalogue-describe", ident],
    queryFn: () => getCouponCatalogueDescribe(ident),
    staleTime: PRIZE_STALE_TIME,
    cacheTime: PRIZE_CACHE_TIME,
    refetchOnMount: "always",
  });

  const prizeCatalogue = useQuery({
    queryKey: ["coupon-catalogue-standard-list"],
    queryFn: () => getCouponCatalogueStandardList(),
    staleTime: COUPON_STALE_TIME,
    cacheTime: COUPON_CACHE_TIME,
  });

  const detailsQuery = useQuery({
    queryKey: ["user-details"],
    queryFn: () => getUserDetails(),
    refetchInterval: DETAILS_REFETCH_INVERVAL,
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
  });

  const prizeListQuery = useQuery({
    queryKey: ["coupon-list"],
    queryFn: () => getCouponList(),
    staleTime: COUPON_STALE_TIME,
    cacheTime: COUPON_CACHE_TIME,
  });

  const wonPrizeListQuery = useQuery({
    queryKey: ["prize-list", "include-gamereward"],
    queryFn: () => getPrizeList(undefined, { includeGameRewards: true }),
    staleTime: PRIZE_STALE_TIME,
    cacheTime: PRIZE_CACHE_TIME,
    enabled: isCouponPlaceholder(ident),
    refetchOnMount: "always",
  });

  const partnerAvailable = prizeCatalogue.data?.partnerAvailable;
  const partnerAvailableCoupon = prizeCatalogue.data?.catalogue?.find(
    (coupon) => coupon.ident === ident,
  )?.partner;

  const partnerRedeemExists = data?.content?.modules?.some(
    (module) => module.type === "partner_redeem",
  );

  const peroidConfig = {
    DAY: "Tag",
    WEEK: "Woche",
    MONTH: "Monat",
    DAYS_BACK: "7 Tage",
  };

  const logosModules = getModuleValue(
    data?.content?.modules,
    partnerRedeemExists ? "partner_redeem" : "online_redeem",
    "metadata.logos",
  );

  useEffect(() => {
    if (!partnerAvailable && partnerAvailableCoupon) {
      NiceModal.show("partner-available-modal");
    }
  }, [partnerAvailable, partnerAvailableCoupon]);

  const gameRewardPrize =
    isCouponPlaceholder(ident) && wonPrizeListQuery.isSuccess
      ? findGameRewardPrizeInList(wonPrizeListQuery.data?.prizes, ident)
      : null;
  const hasGameReward = !!gameRewardPrize;

  useEffect(() => {
    if (!isCouponPlaceholder(ident) || !wonPrizeListQuery.isSuccess) {
      return;
    }

    const gameReward = findGameRewardPrizeInList(
      wonPrizeListQuery.data?.prizes,
      ident,
    );
    if (!gameReward) return;

    const path = getGameRewardPrizeRedeemPath(gameReward);
    if (path && location.pathname !== path) {
      navigate(path, { replace: true });
    }
  }, [
    ident,
    wonPrizeListQuery.isSuccess,
    wonPrizeListQuery.data?.prizes,
    navigate,
    location.pathname,
  ]);

  const redirectToActivation =
    !hasGameReward && shouldRedirectCouponToActivation(data);
  const activationPath = getActivationActivityPath(data?.activation);
  const raffleOutroType = !hasGameReward && getCouponRaffleOutroType(data);
  const showRaffleOutro = !!raffleOutroType;
  const redeemModuleType = partnerRedeemExists
    ? "partner_redeem"
    : "online_redeem";

  function redeemButtonCopy() {
    if (data) {
      if (redirectToActivation) {
        return "Jetzt teilnehmen";
      }
      if (!partnerAvailable && partnerAvailableCoupon) {
        return "Gutschein gesperrt";
      } else if (!isCouponInStock(data)) {
        return "Nicht lieferbar";
      } else if (!data.hasLevel) {
        return "Status unpassend";
      } else if (!data.hasPoints) {
        return "Mehr Punkte sammeln";
      } else if (
        data.periodicLimitExceeded &&
        data.ident.includes("wunschgutschein")
      ) {
        return "Wöchentliche Einlöselimit erreicht ";
      } else if (data.periodicLimitExceeded) {
        return "Einlöselimit erreicht";
      } else if (ident === "vuse_ultra_seeding_box") {
        return "Jetzt einlösen";
      } else {
        return "Gutschein abholen";
      }
    }
  }

  function redeemButtonLock() {
    if (redirectToActivation) {
      return false;
    }
    if (data) {
      if (data.available === false) {
        return true;
      }
    }
  }

  const isSpecial =
    ident === "set_fragrance_2025" || ident === "vuse_ultra_seeding_box";
  const availability = getCouponAvailability(data?.availabilityLevel);

  function redeemAward() {
    fetchData({
      endpoint: "/coupon/redeem",
      payload: {
        catalogue: {
          ident: DOMPurify.sanitize(ident),
        },
      },
      successCallback: (resObj) => {
        setLoading(false);
        refetchData();
        detailsQuery.refetch();
        prizeCatalogue.refetch();
        prizeListQuery.refetch();
        if (isSpecial) {
          NiceModal.show("fragrance-2025-redeem-award-modal", {
            awardName: parseCMSBold(data?.content?.description ?? ""),
            ident: ident,
            bg: getModuleValue(data?.content?.modules, "thumbnail", "file.url"),
            imgDesc: getModuleValue(
              data?.content?.modules,
              "thumbnail",
              "metadata.imgDesc",
            ),
            awardUUID: resObj.data[0].uuid,
            brands: data?.brands,
            logos: logosModules,
          });
        } else {
          NiceModal.show("redeem-award-modal", {
            awardName: parseCMSBold(data?.content?.description ?? ""),
            ident: ident,
            bg: getModuleValue(data?.content?.modules, "thumbnail", "file.url"),
            imgDesc: getModuleValue(
              data?.content?.modules,
              "thumbnail",
              "metadata.imgDesc",
            ),
            awardUUID: resObj.data[0].uuid,
            brands: data?.brands,
            logos: logosModules,
            title: data?.content?.title,
            customModal: data?.content?.modules?.find(
              (module) => module.type === "custom_redem_award_modal",
            ),
          });
        }
      },
      errorCallback: (resObj) => {
        setLoading(false);
        detailsQuery.refetch();
      },
      setLoading: setLoading,
    });
  }

  return (
    <section
      className={`redeem-award-page${showRaffleOutro ? " raffle-pending" : ""}`}
    >
      <PagesHeader
        back
        dangerouslySetInnerHTML={parseCMSBold(data?.content?.description ?? "")}
      />
      <div className="desktop-wrapper">
        {width >= 1020 && (
          <div className="award-img-desktop">
            <img
              src={getModuleValue(
                data?.content?.modules,
                "hero_image",
                "file.url",
              )}
              alt={getModuleValue(
                data?.content?.modules,
                "hero_image",
                "metadata.imgDesc",
              )}
            />
            <BrandLogo brands={data?.brands} />
            {getModuleValue(data?.content?.modules, "text", "custom") && (
              <div className="warning-box">
                <div className="warning-icon">
                  <WarningIcon />
                </div>
                <div
                  className="warning-content"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      getModuleValue(data?.content?.modules, "text", "custom"),
                    ),
                  }}
                ></div>
              </div>
            )}
          </div>
        )}
        <div className="main-content">
          {width < 1020 && (
            <>
              <div className="award-img-mobile">
                <img
                  src={getModuleValue(
                    data?.content?.modules,
                    "thumbnail",
                    "file.url",
                  )}
                  alt={getModuleValue(
                    data?.content?.modules,
                    "thumbnail",
                    "metadata.imgDesc",
                  )}
                />
                <BrandLogo brands={data?.brands} />
              </div>
              {getModuleValue(data?.content?.modules, "text", "custom") && (
                <div className="warning-box">
                  <div className="warning-icon">
                    <WarningIcon />
                  </div>
                  <div
                    className="warning-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        getModuleValue(
                          data?.content?.modules,
                          "text",
                          "custom",
                        ),
                      ),
                    }}
                  ></div>
                </div>
              )}
            </>
          )}
          <div className="award-summary">
            <SectionHeader>
              {showRaffleOutro ? (
                <>{getCouponRaffleOutroSectionTitle(raffleOutroType)}</>
              ) : (
                <>
                  <strong>Einzelheiten</strong> des Angebots
                </>
              )}
            </SectionHeader>
            <div
              className="content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  showRaffleOutro
                    ? getCouponRaffleOutroContent(data)
                    : getModuleValue(
                        data?.content?.modules,
                        redeemModuleType,
                        "metadata.about",
                      ),
                ),
              }}
            />
            {!showRaffleOutro && (
              <>
                {availability && (
                  <p className="availability">
                    <span>Verfügbarkeit:</span> {availability.label}
                    <span className="availability-bar" aria-hidden="true">
                      <span
                        style={{ width: `${availability.progress * 100}%` }}
                      />
                    </span>
                  </p>
                )}
                <p>
                  <span>KOSTET:</span> {data?.price} PUNKTE
                </p>
                <p>
                  <span>Ablaufdatum:</span> {formatDate(data.expirationDate)}
                </p>
                {data.period !== null && (
                  <p>
                    <span>Einlöselimit:</span> 1 pro {peroidConfig[data.period]}
                  </p>
                )}
              </>
            )}
            {width >= 1020 && !showRaffleOutro && (
              <Button
                href={redirectToActivation ? activationPath : undefined}
                action={
                  redirectToActivation
                    ? undefined
                    : () => {
                        setLoading(true);
                        redeemAward();
                      }
                }
                disabled={redeemButtonLock() || loading}
              >
                {redeemButtonCopy()}
              </Button>
            )}
          </div>

          {!showRaffleOutro && logosModules?.length > 0 && (
            <div className="award-info">
              <SectionHeader>
                <strong>Zur Verwendung auf:</strong>
              </SectionHeader>
              <div className="logos">
                {logosModules.map((e, index) => (
                  <img src={e.file.url} alt={e.alt} key={index} />
                ))}
              </div>
            </div>
          )}

          {ident === "set_fragrance_2025" && (
            <div
              className="modals-info"
              onClick={() => NiceModal.show("vuse-fragrance-2025-modal")}
            >
              Inhaltsstoffe ansehen
            </div>
          )}

          {!showRaffleOutro &&
            parseHTMLToText(
              getModuleValue(
                data?.content?.modules,
                redeemModuleType,
                "metadata.howToUse",
              ),
              "li",
              true,
            )?.length !== 0 && (
              <UnderlineButton
                className="how-to-use"
                onClick={() =>
                  NiceModal.show("howToUse-modal", {
                    howToUse: parseHTMLToText(
                      getModuleValue(
                        data?.content?.modules,
                        partnerRedeemExists
                          ? "partner_redeem"
                          : "online_redeem",
                        "metadata.howToUse",
                      ),
                      "li",
                      true,
                    ),
                  })
                }
              >
                Anwendungstipps
              </UnderlineButton>
            )}
        </div>
      </div>
      {width < 1020 && !showRaffleOutro && (
        <StickyButton
          href={redirectToActivation ? activationPath : undefined}
          action={
            redirectToActivation
              ? undefined
              : () => {
                  setLoading(true);
                  redeemAward();
                }
          }
          disabled={redeemButtonLock() || loading}
        >
          {redeemButtonCopy()}
        </StickyButton>
      )}
    </section>
  );
}
